import React, { useState } from 'react';
import { X, Heart, MessageCircle, Send, Bookmark, ChevronLeft, ChevronRight, Volume2 } from 'lucide-react';

const isVideoType = (mediaType) => mediaType === 'VIDEO' || mediaType === 'REELS';
const isFullBleedType = (mediaType) => mediaType === 'REELS' || mediaType === 'STORIES';

function MediaFrame({ url, mediaType, className }) {
  if (!url) {
    return <div className={`bg-[#EFEFEF] flex items-center justify-center text-[#8E8E8E] text-[12px] ${className}`}>No media yet</div>;
  }
  if (isVideoType(mediaType)) {
    return (
      <video
        src={url}
        className={`object-cover ${className}`}
        muted
        loop
        autoPlay
        playsInline
        controls={mediaType === 'VIDEO'}
      />
    );
  }
  return <img src={url} alt="" className={`object-cover ${className}`} />;
}

function FeedPreview({ mediaType, caption, mediaUrls, account }) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [captionExpanded, setCaptionExpanded] = useState(false);
  const activeUrl = mediaUrls[activeIndex];
  const isCarousel = mediaType === 'CAROUSEL' && mediaUrls.length > 1;
  const username = account?.username || 'your_account';
  const captionIsLong = (caption || '').length > 90;
  const captionText = captionExpanded || !captionIsLong ? caption : `${caption.slice(0, 90)}…`;

  return (
    <div className="bg-white rounded-lg overflow-hidden">
      <div className="flex items-center gap-2 px-3 py-2.5">
        {account?.profilePictureUrl ? (
          <img src={account.profilePictureUrl} alt="" className="w-8 h-8 rounded-full object-cover" />
        ) : (
          <div className="w-8 h-8 rounded-full bg-[#0176D3] from-[#E1306C] to-[#F77737] flex items-center justify-center text-white text-[12px] font-bold">
            {username.slice(0, 1).toUpperCase()}
          </div>
        )}
        <span className="text-[13px] font-semibold text-[#080707]">{username}</span>
        <span className="ml-auto text-[#080707] text-[16px] leading-none">•••</span>
      </div>

      <div className="relative w-full aspect-square bg-black">
        <MediaFrame url={activeUrl} mediaType={mediaType} className="w-full h-full" />
        {isCarousel && (
          <>
            <button
              onClick={() => setActiveIndex((i) => (i - 1 + mediaUrls.length) % mediaUrls.length)}
              className="absolute left-1.5 top-1/2 -translate-y-1/2 bg-white/80 rounded-full p-1"
            >
              <ChevronLeft size={14} />
            </button>
            <button
              onClick={() => setActiveIndex((i) => (i + 1) % mediaUrls.length)}
              className="absolute right-1.5 top-1/2 -translate-y-1/2 bg-white/80 rounded-full p-1"
            >
              <ChevronRight size={14} />
            </button>
            <div className="absolute bottom-2 left-0 right-0 flex justify-center gap-1">
              {mediaUrls.map((u, i) => (
                <span key={u} className={`w-1.5 h-1.5 rounded-full ${i === activeIndex ? 'bg-[#0176D3]' : 'bg-white/70'}`} />
              ))}
            </div>
          </>
        )}
      </div>

      <div className="px-3 pt-2 pb-3 space-y-2">
        <div className="flex items-center gap-4 text-[#080707]">
          <Heart size={22} />
          <MessageCircle size={22} />
          <Send size={22} />
          <Bookmark size={22} className="ml-auto" />
        </div>
        {(caption || '').length > 0 && (
          <div className="text-[13px] text-[#080707] leading-snug">
            <span className="font-semibold mr-1">{username}</span>
            {captionText}
            {captionIsLong && !captionExpanded && (
              <button onClick={() => setCaptionExpanded(true)} className="text-[#8E8E8E] ml-1">more</button>
            )}
          </div>
        )}
        {!caption && <div className="text-[12px] text-[#8E8E8E] italic">No caption yet</div>}
      </div>
    </div>
  );
}

function FullBleedPreview({ mediaType, caption, mediaUrls, account }) {
  const username = account?.username || 'your_account';
  const url = mediaUrls[0];
  const isStory = mediaType === 'STORIES';

  return (
    <div className="relative w-full h-full bg-black overflow-hidden">
      <MediaFrame url={url} mediaType={mediaType} className="w-full h-full" />
      <div className="absolute inset-0 bg-[#0176D3] from-black/40 via-transparent to-black/50 pointer-events-none" />

      {isStory && (
        <div className="absolute top-2 left-2 right-2 flex gap-1">
          <div className="h-0.5 flex-1 rounded-full bg-white" />
        </div>
      )}

      <div className="absolute top-4 left-3 flex items-center gap-2">
        {account?.profilePictureUrl ? (
          <img src={account.profilePictureUrl} alt="" className="w-7 h-7 rounded-full object-cover border border-white/70" />
        ) : (
          <div className="w-7 h-7 rounded-full bg-[#0176D3] from-[#E1306C] to-[#F77737] flex items-center justify-center text-white text-[11px] font-bold">
            {username.slice(0, 1).toUpperCase()}
          </div>
        )}
        <span className="text-white text-[13px] font-semibold drop-shadow">{username}</span>
      </div>

      {!isStory && (
        <div className="absolute right-2.5 bottom-16 flex flex-col items-center gap-4 text-white">
          <Heart size={24} />
          <MessageCircle size={24} />
          <Send size={24} />
          <Volume2 size={20} className="opacity-80" />
        </div>
      )}

      {caption && (
        <div className="absolute left-3 right-14 bottom-6 text-white text-[12px] leading-snug drop-shadow">
          {!isStory && <span className="font-semibold mr-1">{username}</span>}
          {caption}
        </div>
      )}
      {!url && <div className="absolute inset-0 flex items-center justify-center text-white/70 text-[12px]">No media yet</div>}
    </div>
  );
}

export default function InstagramPostPreview({ open, onClose, mediaType, caption, mediaUrls, account }) {
  if (!open) return null;
  const fullBleed = isFullBleedType(mediaType);

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-[#080707]/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-10 flex flex-col items-center gap-3">
        <button onClick={onClose} className="self-end text-white/90 hover:text-white flex items-center gap-1 text-[13px]">
          <X size={16} /> Close preview
        </button>

        <div className="w-[300px] bg-black rounded-[46px] p-3 shadow-[0_50px_100px_-20px_rgba(0,0,0,0.5)] border-[6px] border-[#1F2C33]">
          <div className="absolute left-1/2 -translate-x-1/2 mt-0 w-20 h-5 bg-[#1F2C33] rounded-b-2xl" style={{ marginTop: '-2px' }} />
          <div className="bg-white rounded-[34px] overflow-hidden h-[560px] flex flex-col">
            {fullBleed ? (
              <FullBleedPreview mediaType={mediaType} caption={caption} mediaUrls={mediaUrls} account={account} />
            ) : (
              <div className="overflow-y-auto">
                <FeedPreview mediaType={mediaType} caption={caption} mediaUrls={mediaUrls} account={account} />
              </div>
            )}
          </div>
        </div>

        <p className="text-[11px] text-white/70 max-w-[300px] text-center">
          Preview is representative — exact spacing, fonts, and like/comment counts depend on the real Instagram app and audience.
        </p>
      </div>
    </div>
  );
}
